import { createInertiaApp, router } from '@inertiajs/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { registerServiceWorker } from '../lib/push'
import { track } from '../lib/telemetry'
import '../styles/tokens.css'
import '../styles/app.css'
import '../styles/primitives.css'

// Register the service worker (handles Web Push) once the page has loaded.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => { registerServiceWorker() })
}

// Apply the saved calm (low-distraction) theme before first paint so there's no flash.
try {
  if (localStorage.getItem('acorn-calm') === '1') {
    document.documentElement.setAttribute('data-calm', 'true')
  }
} catch { /* localStorage unavailable — calm mode just won't persist */ }

// Frontend error monitoring → Sentry → Linear. The DSN is injected by the Rails
// layout in production only (so dev never phones home). Mirrors the server-side
// config: no PII, light sampling.
const sentryDsn = document.querySelector('meta[name="sentry-dsn"]')?.content
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: 'production',
    sendDefaultPii: false,
    // Browser tracing turns tracesSampleRate into real page-load / navigation /
    // fetch spans — the "where is it slow" signal for the client. Without this
    // integration the sample rate had nothing to sample.
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
  })
}

// Auto-track screen usage: one "ui.page_view" per Inertia navigation, tagged with
// the page component (not the raw URL — no ids in the telemetry). This is the
// "which screens get used" signal, captured in one place instead of per-page.
// Also attach a PII-free Sentry user (id + role) so errors are attributable.
function identify(page) {
  try {
    const user = page?.props?.auth?.user
    if (user) Sentry.setUser({ id: user.id, role: user.role })
  } catch { /* non-fatal */ }
}
router.on('navigate', (event) => {
  const page = event.detail?.page
  identify(page)
  track('ui.page_view', { component: page?.component })
})

// A calm, on-brand fallback if a screen crashes — kids/parents see this, not a
// blank page, and the error is reported.
function CrashFallback() {
  return (
    <div className="page" style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '2.4rem' }}>🌳</div>
      <h1 style={{ fontSize: '1.4rem', marginTop: 8 }}>Something went sideways</h1>
      <p className="muted" style={{ marginTop: 8 }}>A little hiccup on our end. Refresh the page to keep going.</p>
      <button type="button" className="btn" style={{ marginTop: 16 }} onClick={() => window.location.reload()}>Refresh</button>
    </div>
  )
}

createInertiaApp({
  pages: "../pages",

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    visitOptions: () => {
      return { queryStringArrayFormat: "brackets" }
    },
  },

  setup({ el, App, props }) {
    // Reuse the root across re-invocations (Vite HMR) so we never call
    // createRoot twice on the same element.
    const root = el.__inertiaRoot ?? (el.__inertiaRoot = createRoot(el))
    root.render(
      <StrictMode>
        <Sentry.ErrorBoundary fallback={<CrashFallback />}>
          <App {...props} />
        </Sentry.ErrorBoundary>
      </StrictMode>,
    )
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_javascript_tag "inertia.jsx" %> to the Inertia-specific layout instead.',
    )
  }
})
